import React from "react";
import {
  Box,
  styled,
  Chip,
  Table,
  TableRow,
  TableBody,
  TableHead,
  TableCell,
  TableContainer,
  Paper,
} from "@material-ui/core";
import { Skeleton } from "@material-ui/lab";

import SlotManagementViewController, {
  Props,
  S,
  emptyData,
} from "./SlotManagementViewController.web";

import { colors } from "../../utilities/src/Colors";

const Strings = {
  pageTitle: "Slot Management",
  back: "Back",
  edit: "Edit",
  noData: "Data Not Found!",
  mobileService: "Mobile Service",
  threshold: "Threshold",
  activated: "Activated",
  regions: "Regions",
  areas: "Areas",
  stores: "Stores",
  slots: "Slots",
  slotOverrides: "Slot Overrides",
  slotType: "Slot Type",
  window: "Window",
  interval: "Interval",
  slotsPerDay: "Slots / Day",
  perSlot: "Capacity Per Slot",
  startTime: "Start Time",
  endTime: "End Time",
  status: "Status",
  startDate: "Start Date",
  endDate: "End Date",
  reason: "Reason",
  noViewData: "----",
};

export default class SlotManagementView extends SlotManagementViewController {
  constructor(props: Props) {
    super(props);
    this.state = {
      isLoading: false,
      noData: false,
      data: emptyData,
    } as S;
  }

  renderChipList = (items: Array<{ id: number; name: string }>) => {
    if (!items || items.length === 0) return <ValueText>{Strings.noViewData}</ValueText>;
    return (
      <ChipContainer>
        {items.map((item) => (
          <Chip key={item.id} label={item.name} style={chipStyle} />
        ))}
      </ChipContainer>
    );
  };

  renderStatusChip = (active: boolean) => (
    <Chip
      label={active ? "Active" : "Inactive"}
      style={{
        backgroundColor: active ? "#E8F5E9" : "#FFEBEE",
        color: active ? "#2E7D32" : "#C62828",
        fontWeight: 600,
      }}
    />
  );

  render() {
    const { isLoading, noData, data } = this.state;
    const attr = data.attributes;

    return (
      <MainContainer>
        <HeadingContainer>
          <Heading>{Strings.pageTitle}</Heading>
          <BottomContainer>
            <button
              data-test-id="btn-back"
              onClick={() => this.goBack()}
              className="cancel"
            >
              {Strings.back}
            </button>
            <button
              data-test-id="btn-edit"
              onClick={() =>
                this.props.navigation.navigate("SlotManagementEdit", {
                  id: this.props.navigation.getParam("id"),
                })
              }
              className="add"
            >
              {Strings.edit}
            </button>
          </BottomContainer>
        </HeadingContainer>

        {isLoading && <LoadingComponent />}

        {!isLoading && noData && (
          <span style={{ color: "#EC4E20" }}>{Strings.noData}</span>
        )}

        {!isLoading && !noData && (
          <>
            <WhiteContainer>
              <GridContainer>
                <div>
                  <TextLabel>{Strings.mobileService}</TextLabel>
                  <ValueText>
                    {attr.mobile_service?.name || Strings.noViewData}
                  </ValueText>
                </div>
                <div>
                  <TextLabel>{Strings.slotType}</TextLabel>
                  <ValueText style={{ textTransform: "capitalize" }}>
                    {attr.slot_type || Strings.noViewData}
                  </ValueText>
                </div>
                <div>
                  <TextLabel>{Strings.window}</TextLabel>
                  <ValueText>
                    {attr.start_time && attr.end_time
                      ? `${attr.start_time} – ${attr.end_time}`
                      : Strings.noViewData}
                  </ValueText>
                </div>
                <div>
                  <TextLabel>{Strings.interval}</TextLabel>
                  <ValueText>
                    {attr.interval_minutes
                      ? `${attr.interval_minutes} min`
                      : Strings.noViewData}
                  </ValueText>
                </div>
                <div>
                  <TextLabel>{Strings.slotsPerDay}</TextLabel>
                  <ValueText>{attr.slots_per_day ?? 0}</ValueText>
                </div>
                <div>
                  <TextLabel>{Strings.threshold}</TextLabel>
                  <ValueText>{attr.threshold}</ValueText>
                </div>
                <div>
                  <TextLabel>{Strings.perSlot}</TextLabel>
                  <ValueText>{attr.per_slot_capacity ?? 0}</ValueText>
                </div>
                <div>
                  <TextLabel>{Strings.activated}</TextLabel>
                  {this.renderStatusChip(attr.activated)}
                </div>
              </GridContainer>

              <SectionDivider />

              <GridContainer>
                <div>
                  <TextLabel>{Strings.regions}</TextLabel>
                  {this.renderChipList(attr.regions)}
                </div>
                <div>
                  <TextLabel>{Strings.areas}</TextLabel>
                  {this.renderChipList(attr.areas)}
                </div>
                <div>
                  <TextLabel>{Strings.stores}</TextLabel>
                  {this.renderChipList(attr.stores)}
                </div>
              </GridContainer>
            </WhiteContainer>

            {/* Slots Table */}
            <SectionTitle>{Strings.slots}</SectionTitle>
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow style={{ background: "#204B9C" }}>
                    <StyledHeaderCell>{Strings.startTime}</StyledHeaderCell>
                    <StyledHeaderCell>{Strings.endTime}</StyledHeaderCell>
                    <StyledHeaderCell>{Strings.status}</StyledHeaderCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {attr.slots && attr.slots.length > 0 ? (
                    // generate_slots! deactivates superseded rows instead of
                    // deleting them (bookings reference them), so this list grows
                    // over time — show the live schedule first.
                    [...attr.slots]
                      .sort((first, second) => {
                        if (first.activated !== second.activated) {
                          return first.activated ? -1 : 1;
                        }
                        return first.start_time.localeCompare(second.start_time);
                      })
                      .map((slot) => (
                        <TableRow key={slot.id}>
                          <TableCell>{slot.start_time}</TableCell>
                          <TableCell>{slot.end_time}</TableCell>
                          <TableCell>
                            {this.renderStatusChip(slot.activated)}
                          </TableCell>
                        </TableRow>
                      ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={3} align="center">
                        No slots found
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            {/* Slot Overrides Table */}
            <SectionTitle>{Strings.slotOverrides}</SectionTitle>
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow style={{ background: "#204B9C" }}>
                    <StyledHeaderCell>{Strings.startDate}</StyledHeaderCell>
                    <StyledHeaderCell>{Strings.endDate}</StyledHeaderCell>
                    <StyledHeaderCell>{Strings.startTime}</StyledHeaderCell>
                    <StyledHeaderCell>{Strings.endTime}</StyledHeaderCell>
                    <StyledHeaderCell>{Strings.reason}</StyledHeaderCell>
                    <StyledHeaderCell>{Strings.status}</StyledHeaderCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {attr.slot_overrides && attr.slot_overrides.length > 0 ? (
                    attr.slot_overrides.map((override) => (
                      <TableRow key={override.id}>
                        <TableCell>{override.start_date}</TableCell>
                        <TableCell>{override.end_date}</TableCell>
                        <TableCell>{override.start_time || Strings.noViewData}</TableCell>
                        <TableCell>{override.end_time || Strings.noViewData}</TableCell>
                        <TableCell>{override.reason}</TableCell>
                        <TableCell>
                          {this.renderStatusChip(override.activated)}
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={6} align="center">
                        No slot overrides found
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        )}
      </MainContainer>
    );
  }
}

// Customizable Area Start
const MainContainer = styled(Box)({
  paddingTop: "25px",
});

const Heading = styled("h2")({
  color: "#000",
  fontSize: 24,
  fontWeight: 600,
  margin: 0,
  "@media only screen and (max-width: 1024px)": {
    fontSize: "16px",
  },
});

const HeadingContainer = styled("div")({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  marginBottom: 25,
});

const WhiteContainer = styled("div")({
  borderRadius: 12,
  background: "#FFF",
  padding: "24px 32px",
  boxShadow:
    "0px 8px 32px 0px rgba(0, 0, 0, 0.06), 0px 4px 8px 0px rgba(0, 0, 0, 0.03)",
});

const GridContainer = styled("div")({
  display: "grid",
  gridGap: 32,
  gridTemplateColumns: "1fr 1fr 1fr",
  "@media only screen and (max-width: 1024px)": {
    gridTemplateColumns: "1fr 1fr",
  },
});

const SectionDivider = styled("hr")({
  border: "none",
  borderTop: "1px solid #E2E8F0",
  margin: "24px 0",
});

const TextLabel = styled("label")({
  display: "block",
  fontSize: 14,
  lineHeight: "20px",
  fontWeight: 600,
  paddingBottom: 4,
  color: "#64748B",
  "@media only screen and (max-width: 1024px)": {
    fontSize: "12px",
  },
});

const ValueText = styled("div")({
  fontSize: 16,
  fontWeight: 500,
  color: "#1E293B",
  fontFamily: "Montserrat",
  "@media only screen and (max-width: 1024px)": {
    fontSize: "13px",
  },
});

const ChipContainer = styled("div")({
  display: "flex",
  flexWrap: "wrap",
  gap: 6,
});

const chipStyle = {
  backgroundColor: "#E8EAF6",
  color: "#204B9C",
  fontWeight: 500,
};

const SectionTitle = styled("h3")({
  fontSize: 20,
  fontWeight: 600,
  color: "#000",
  marginTop: 32,
  marginBottom: 16,
  fontFamily: "Montserrat",
});

const StyledHeaderCell = styled(TableCell)({
  color: "#FFFFFF !important",
  fontWeight: 600,
  fontFamily: "Montserrat",
});

const LoadingComponent = styled("div")({
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  height: "200px",
  fontSize: 16,
  color: "#204B9C",
  "&::after": {
    content: '"Loading..."',
  },
});

const BottomContainer = styled("div")({
  gap: 32,
  display: "flex",
  "& > button": {
    fontSize: 16,
    padding: "12px 24px",
    minWidth: 126,
    border: "none",
    fontWeight: 700,
    borderRadius: 8,
    cursor: "pointer",
    lineHeight: "24px",
    alignItems: "center",
    display: "inline-flex",
    justifyContent: "center",
    fontFamily: "Montserrat",
  },
  "& > .cancel": {
    color: "#64748B",
    background: "#F1F5F9",
  },
  "& > .add": {
    color: "white",
    background: "#204B9C",
  },
});
// Customizable Area End
